import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, KeyRound } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cardInitial = { opacity: 0, y: 30 };
const cardAnimate = { opacity: 1, y: 0 };
const cardTransition = { duration: 0.6, ease: [0.22, 1, 0.36, 1] };

export default function ChangePassword() {
  const nav = useNavigate();
  const [role, setRole] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setRole(data.role))
      .catch(() => nav("/admin/login"));
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true });
      nav(role === "packager" ? "/packager" : "/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#052F24] flex items-center justify-center px-5 grain">
      <motion.div
        initial={cardInitial}
        animate={cardAnimate}
        transition={cardTransition}
        className="w-full max-w-md bg-[#F6F5F2] rounded-3xl p-9 sm:p-11"
      >
        <Logo />
        <div className="mt-10 mb-8">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#052F24]/45">
            <KeyRound size={13} /> Security
          </span>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-3">Set a new password</h1>
          <p className="text-sm text-[#052F24]/55 font-light mt-2">You must change your temporary password before continuing.</p>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <input
            type="password"
            placeholder="Current (temporary) password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full bg-white border border-[#E5E5E0] rounded-xl px-4 py-3.5 outline-none focus:border-[#052F24] transition-colors"
            data-testid="change-password-current"
          />
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full bg-white border border-[#E5E5E0] rounded-xl px-4 py-3.5 outline-none focus:border-[#052F24] transition-colors"
            data-testid="change-password-new"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full bg-white border border-[#E5E5E0] rounded-xl px-4 py-3.5 outline-none focus:border-[#052F24] transition-colors"
            data-testid="change-password-confirm"
          />
          {error && <p className="text-sm text-red-600" data-testid="change-password-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#052F24] text-[#DFFF00] font-bold py-4 rounded-xl hover:bg-[#0a3d30] transition-colors disabled:opacity-50"
            data-testid="change-password-submit"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
